import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { FlaskConical, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';


export default function LabResults() {
  const [results, setResults] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    test_name: '',
    test_date: new Date().toISOString().split('T')[0],
    results: {},
    notes: ''
  });

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      const response = await fetch(`${API}/lab-results`, {
        credentials: 'include'
      });
      if (response.ok) {
        setResults(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API}/lab-results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Lab result saved');
        setShowDialog(false);
        setFormData({
          test_name: '',
          test_date: new Date().toISOString().split('T')[0],
          results: {},
          notes: ''
        });
        fetchResults();
      }
    } catch (error) {
      toast.error('Failed to save lab result');
    }
  };

  const handleDelete = async (resultId) => {
    if (!confirm('Delete this lab result?')) return;

    try {
      const response = await fetch(`${API}/lab-results/${resultId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Lab result deleted');
        fetchResults();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="lab-results-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold mb-2">Lab Results</h1>
          <p className="text-muted-foreground">Track your blood work and biomarkers</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button data-testid="add-lab-result-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Result
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Lab Result</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Test Name</Label>
                <Input
                  value={formData.test_name}
                  onChange={(e) => setFormData({...formData, test_name: e.target.value})}
                  placeholder="e.g., IGF-1, Testosterone, CBC"
                  required
                />
              </div>
              <div>
                <Label>Test Date</Label>
                <Input
                  type="date"
                  value={formData.test_date}
                  onChange={(e) => setFormData({...formData, test_date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Add any observations or context..."
                />
              </div>
              <Button type="submit" className="w-full">Save Result</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {results.length === 0 ? (
        <Card className="p-12 text-center glass-card">
          <FlaskConical className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No lab results yet. Click "Add Result" to track your biomarkers.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {results.map((result) => (
            <Card key={result.result_id} className="p-6 glass-card">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <FlaskConical className="h-5 w-5 text-primary" />
                    <h3 className="text-xl font-bold">{result.test_name}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Date: {new Date(result.test_date).toLocaleDateString()}
                  </p>
                  {result.notes && (
                    <p className="text-muted-foreground">{result.notes}</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(result.result_id)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
